import type { UserContentBlock } from '@runloop/reflex-contract';

/**
 * A typed `/model` command as the host reads it. `switch` is `/model <id>`,
 * made by the host like the picker. `report` is a bare `/model`, passed to the
 * harness. `invalid` is anything else (several words, attachments), refused so
 * nothing the harness would read as a switch gets past the host's checks.
 */
export type ModelCommand =
  | { kind: 'switch'; model: string }
  | { kind: 'report' }
  | { kind: 'invalid' };

/**
 * Read a message's text as a `/model` command, or `null`. Case-insensitive, so
 * no spelling a harness might accept slips through.
 */
export function parseModelCommand(text: string): ModelCommand | null {
  const words = text.trim().split(/\s+/);
  if (words[0]?.toLowerCase() !== '/model') return null;
  if (words.length === 1) return { kind: 'report' };
  if (words.length === 2) return { kind: 'switch', model: words[1]! };
  return { kind: 'invalid' };
}

/** {@link parseModelCommand} over a message's content blocks. */
export function modelCommandFromContent(content: readonly UserContentBlock[]): ModelCommand | null {
  const text = content
    .filter((block): block is Extract<UserContentBlock, { type: 'text' }> => block.type === 'text')
    .map((block) => block.text)
    .join('\n');
  const command = parseModelCommand(text);
  if (command && content.some((block) => block.type !== 'text')) return { kind: 'invalid' };
  return command;
}
